import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IndianRupee, TrendingUp, BarChart3, ArrowUpRight, ArrowDownRight, Home, Layers, Target, Database } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const COLORS = ['#1E5FAD', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'];

export default function SalesIntelligence() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const salesValue = d.sales_value || 0;
  const salesCount = d.sales_count || 0;
  const sba = d.sba || 0;
  const cancelValue = d.cancel_value || 0;
  const cancelCount = d.cancel_count || 0;
  const netSales = d.net_sales || 0;
  const netCount = d.net_sales_count || 0;
  const netSba = d.net_sba || 0;
  const byProject = d.sales_by_project || [];
  const trend = d.sales_trend || [];
  const cancelByProject = d.cancel_by_project || [];
  const hasData = salesValue > 0 || byProject.length > 0 || salesCount > 0;

  // Analytics
  const avgPriceSqft = sba > 0 ? salesValue / sba : 0;
  const cancelRate = salesCount > 0 ? ((cancelCount / salesCount) * 100).toFixed(1) : 0;
  const topProject = byProject[0];
  const topPct = topProject && salesValue > 0 ? ((topProject.value / salesValue) * 100).toFixed(1) : 0;
  const top3Value = byProject.slice(0, 3).reduce((s, p) => s + p.value, 0);
  const top3Pct = salesValue > 0 ? ((top3Value / salesValue) * 100).toFixed(1) : 0;
  const projectData = byProject.slice(0, 15).map(p => ({ ...p, pct: salesValue > 0 ? ((p.value / salesValue) * 100).toFixed(1) : 0 }));

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="sales-intelligence-page">
      <div><h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Sales Intelligence</h1><p className="text-xs sm:text-sm text-slate-500 mt-0.5">Revenue, units, and project-wise performance</p></div>
      <div className="empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Sales Data for This Period</p><p className="text-sm text-slate-400 mt-1">Upload Sales Table or select a different period</p></div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="sales-intelligence-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Sales Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Revenue, units, and project-wise performance</p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      <AIPageInsight token={token} period={period} refDate={refDate} context={`Sales: ${fmt(salesValue)}, Net: ${fmt(netSales)}, Units: ${salesCount}, Cancels: ${fmt(cancelValue)} (${cancelCount} units), Cancel Rate: ${cancelRate}%, Top: ${topProject?.name || 'N/A'} (${fmt(topProject?.value)}), Top 3 share: ${top3Pct}%`} pageTitle="Sales" />

      {/* KPI Strip - 3 rows of key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><IndianRupee className="h-4 w-4 text-blue-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Gross Sales</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{fmt(salesValue)}</p>
          <p className="text-[9px] text-slate-500 mt-1">{salesCount.toLocaleString()} units</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><ArrowDownRight className="h-4 w-4 text-red-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Cancellations</span></div>
          <p className="text-lg sm:text-xl font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>{fmt(cancelValue)}</p>
          <p className="text-[9px] text-slate-500 mt-1">{cancelCount} units ({cancelRate}%)</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><TrendingUp className="h-4 w-4 text-emerald-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Net Sales</span></div>
          <p className="text-lg sm:text-xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope' }}>{fmt(netSales)}</p>
          <p className="text-[9px] text-slate-500 mt-1">{netCount.toLocaleString()} net units</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-1.5 mb-1.5"><Home className="h-4 w-4 text-indigo-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Avg ₹/Sq.ft</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{avgPriceSqft > 0 ? `₹${Math.round(avgPriceSqft).toLocaleString('en-IN')}` : '--'}</p>
          <p className="text-[9px] text-slate-500 mt-1">{sba > 0 ? `${Math.round(sba).toLocaleString('en-IN')} Sq.ft` : '--'}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales by Project - Horizontal Bars */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="sales-project-chart">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#1E5FAD]" /><CardTitle className="text-sm font-semibold text-slate-900">Revenue by Project</CardTitle></div>
              <span className="text-[10px] text-slate-400">{byProject.length} projects</span>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            {projectData.length > 0 ? (
              <div className="space-y-1.5">
                {projectData.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2">
                    <span className="text-[10px] font-medium text-slate-600 w-28 sm:w-36 truncate flex-shrink-0">{p.name}</span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-sm overflow-hidden">
                      <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${Math.max(2, parseFloat(p.pct))}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-700 w-16 text-right flex-shrink-0">{fmt(p.value)}</span>
                    <span className="text-[9px] text-slate-400 w-10 text-right flex-shrink-0">{p.pct}%</span>
                  </div>
                ))}
              </div>
            ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">No project data</div>}
          </CardContent>
        </Card>

        {/* Cancellation by Project */}
        <Card className="border-slate-200 shadow-sm" data-testid="sales-cancel-chart">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center gap-2"><ArrowDownRight className="h-4 w-4 text-red-500" /><CardTitle className="text-sm font-semibold text-slate-900">Cancellations by Project</CardTitle></div>
          </CardHeader>
          <CardContent className="pt-3">
            {cancelByProject.length > 0 ? (
              <div className="space-y-2">
                {cancelByProject.slice(0, 8).map((p, i) => (
                  <div key={p.name} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                    <span className="text-[10px] font-medium text-slate-600 truncate flex-1">{p.name}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[10px] text-red-600 font-semibold">{fmt(p.value)}</span>
                      <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{p.count} units</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">No cancellations</div>}
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card className="border-slate-200 shadow-sm" data-testid="sales-trend-chart">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#1E5FAD]" /><CardTitle className="text-sm font-semibold text-slate-900">Monthly Sales Trend (Conversion Date)</CardTitle></div>
        </CardHeader>
        <CardContent className="pt-3">
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={fmt} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={v => [fmt(v), "Sales"]} />
                <Area type="monotone" dataKey="value" fill="#1E5FAD20" stroke="#1E5FAD" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">Trend data available with sales data</div>}
        </CardContent>
      </Card>

      {/* Insights */}
      {hasData && (
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-blue-50/30" data-testid="sales-insights">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900">Key Insights</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {topProject && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <ArrowUpRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-slate-800">Top Revenue: {topProject.name}</p><p className="text-[10px] text-slate-500 mt-0.5">{fmt(topProject.value)} ({topPct}% share), {topProject.count} units</p></div>
                </div>
              )}
              {parseFloat(cancelRate) > 0 && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <ArrowDownRight className={`h-4 w-4 mt-0.5 flex-shrink-0 ${parseFloat(cancelRate) > 10 ? 'text-red-500' : 'text-amber-500'}`} />
                  <div><p className="text-xs font-medium text-slate-800">Cancel Rate: {cancelRate}%</p><p className="text-[10px] text-slate-500 mt-0.5">{cancelCount} units cancelled worth {fmt(cancelValue)}</p></div>
                </div>
              )}
              {parseFloat(top3Pct) > 0 && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <Layers className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-slate-800">Revenue Concentration</p><p className="text-[10px] text-slate-500 mt-0.5">Top 3 projects contribute {top3Pct}% ({fmt(top3Value)})</p></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
